import { Button, Divider } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import clsx from 'clsx'
import Card from 'components/Card'
import {
    CHATBOT_KNOWLEDGE_WIDGET_ID,
    STATUS_BADGE_CLASSES,
    STATUS_LABEL,
    TRIGGER_LABEL,
} from './chatbotKnowledgeConstants'
import type { ChatbotKnowledgeIndex, KnowledgeIndexStatus } from './types'
import { useChatbotKnowledgeIndex } from './useChatbotKnowledgeIndex'

interface ChatbotKnowledgeWidgetProps {
    providerId: number
}

/**
 * What the chatbot can actually answer from, and a way to fix it.
 *
 * The failure this exists to surface is quiet: a bot goes live, the crawl never
 * ran or returned nothing, and the only symptom is the bot saying it doesn't
 * know — which nobody sees until a provider complains.
 */
const ChatbotKnowledgeWidget = ({ providerId }: ChatbotKnowledgeWidgetProps) => {
    const { data, isLoading, error, requestCrawl, isRequesting, requestError } =
        useChatbotKnowledgeIndex(providerId)

    const isCrawling = data?.isCrawling === true
    const hasNoWebsite = data?.status === 'no_website'

    const content = (() => {
        if (isLoading) {
            return <LoadingDots />
        }
        if (error) {
            return <p className="text-md text-neutral-500">Could not load knowledge index.</p>
        }
        if (data) {
            return <ChatbotKnowledgeBody data={data} />
        }
        return <p className="text-md text-neutral-500">No crawl has ever been requested.</p>
    })()

    return (
        <Card
            id={CHATBOT_KNOWLEDGE_WIDGET_ID}
            icon="smart_toy"
            header="Chatbot knowledge"
            topRight={data && <StatusBadge status={data.status} isUsable={data.isUsable} />}>
            <Divider m />
            {content}
            {!isLoading && !error && (
                <>
                    <Divider m />
                    <Button
                        name="text"
                        onClick={requestCrawl}
                        disabled={isRequesting || isCrawling || hasNoWebsite}>
                        {crawlButtonLabel({ isRequesting, isCrawling, hasNoWebsite })}
                    </Button>
                    {requestError && (
                        <p className="text-sm text-red-700 break-words">{requestError}</p>
                    )}
                </>
            )}
        </Card>
    )
}

interface CrawlButtonLabelArgs {
    isRequesting: boolean
    isCrawling: boolean
    hasNoWebsite: boolean
}

const crawlButtonLabel = ({
    isRequesting,
    isCrawling,
    hasNoWebsite,
}: CrawlButtonLabelArgs): string => {
    if (isRequesting) {
        return 'Requesting…'
    }
    if (isCrawling) {
        return 'Crawl in progress'
    }
    if (hasNoWebsite) {
        return 'No website to crawl'
    }
    return 'Request crawl'
}

interface StatusBadgeProps {
    status: KnowledgeIndexStatus
    isUsable: boolean
}

const StatusBadge = ({ status, isUsable }: StatusBadgeProps) => (
    <span
        className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-sm font-semibold',
            // A completed crawl with nothing in it reads as green everywhere else;
            // it is the single most misleading state this panel can show.
            status === 'completed' && !isUsable
                ? 'bg-red-100 !text-red-700'
                : STATUS_BADGE_CLASSES[status],
        )}>
        {status === 'completed' && !isUsable ? 'Empty' : STATUS_LABEL[status]}
    </span>
)

interface ChatbotKnowledgeBodyProps {
    data: ChatbotKnowledgeIndex
}

const isStalledCrawl = (data: ChatbotKnowledgeIndex): boolean =>
    (data.status === 'queued' || data.status === 'running') && !data.isCrawling

const ChatbotKnowledgeBody = ({ data }: ChatbotKnowledgeBodyProps) => (
    <div className="space-y-3 text-md">
        {data.status === 'completed' && !data.isUsable && (
            <p className="text-red-700">
                The crawl finished but produced no content, so the bot has nothing to answer from.
            </p>
        )}

        {/* Otherwise the badge says "Queued" while the button offers a new crawl,
            which reads like a bug rather than the recovery it is. */}
        {isStalledCrawl(data) && (
            <p className="text-red-700">
                This crawl stopped reporting hours ago, so its outcome was lost. Requesting a new
                one is safe.
            </p>
        )}

        <dl className="grid grid-cols-2 gap-y-2">
            <Field
                label="Chunks"
                value={data.chunkCount === null ? '—' : String(data.chunkCount)}
            />
            <Field label="Last indexed" value={formatMoment(data.lastIndexedAt)} />
            <Field label="Last requested" value={formatMoment(data.lastRequestedAt)} />
            <Field
                label="Requested by"
                value={data.triggerSource ? TRIGGER_LABEL[data.triggerSource] : '—'}
            />
        </dl>

        {data.lastError && (
            <p className="text-sm text-neutral-500 break-words">Last error: {data.lastError}</p>
        )}
    </div>
)

interface FieldProps {
    label: string
    value: string
}

const Field = ({ label, value }: FieldProps) => (
    <div>
        <dt className="text-[10px] text-neutral-400 uppercase tracking-wide">{label}</dt>
        <dd className="text-neutral-900">{value}</dd>
    </div>
)

const formatMoment = (value: string | null): string =>
    value === null ? 'Never' : new Date(value).toLocaleString('nl-NL')

export default ChatbotKnowledgeWidget
